/* ---------------------------------------------------------------------------
   The rating form, and the answer beside it.

   THE WHOLE ELEVEN-SHEET WORKBOOK ON ONE SCREEN, which the 2009 program could
   not do: GENERAL, LAYOUT, DFDLLL, CCLFM, LRLFM, POSTLL, POSTLFM, CCASM, LRASM,
   POSTASM and POSTSUM are eighteen inputs and one table of results. The
   navigation between them was Excel's constraint, not the method's.

   IT COMPUTES AS YOU TYPE, and the result panel is honest that this is a
   preview. The engine runs in this bundle for the screen and again in the Worker
   for the record; the stored number is the deployment's. See the header of
   src/rating/input.ts.

   EVERY FIELD CARRIES WHERE IT CAME FROM. The picker beside each value is not
   decoration — it is the platform rule that a rating prints its provenance, and
   the reason the form does not silently pre-fill the unit weights that appear in
   all sixteen reference workbooks. A value the application supplied and nobody
   looked at gets attributed to the engineer who signed the rating.

   EMPTY STAYS EMPTY. No field defaults. Excel reads a blank as zero and would
   rate this slab with no asphalt on it and no damage to it; the form lists what
   is still missing instead, and the Save button is not offered until nothing is.
   --------------------------------------------------------------------------- */

import { h } from '../../core/dom.ts';
import { button, card, cardHead, fact, seg, tag, type TagRank } from '../../components/widgets.ts';
import {
  correctionsFor,
  displayRf,
  displayTonnes,
  eraRowFor,
  ratePcdgSimple,
  rateRcdgSimple,
  rateRcfsSimple,
  type PcdgInput,
  type RatingResult,
  type RcdgInput,
  type RcfsInput,
} from '../../cbplr/index.ts';
import {
  FAMILIES,
  FAMILY_LABEL,
  MATERIAL_FIELDS,
  SOURCE_LABEL,
  SOURCE_MARK,
  SOURCE_TIERS,
} from '../store.ts';
import { can, whyNot, type FieldSpec, type SourceTier, type State } from '../store.ts';
import { assemble, fieldsForDraft, type Blocker } from '../input.ts';
import { actions } from '../actions.ts';
import { ratingCharts } from './ratingCharts.ts';
import {
  DAMAGE_LABEL,
  NO_LOSS,
  assumedDamage,
  assumedDamageLine,
  damageFromInspection,
  damageKeys,
  unsavableDamage,
} from '../damage.ts';

/* --------------------------- one field --------------------------- */

function fieldRow(
  spec: FieldSpec,
  value: string,
  tier: SourceTier | undefined,
  blocker: Blocker | undefined,
  editable: boolean,
): HTMLElement {
  const id = `f-${spec.key.replace(/\./g, '-')}`;
  return h(
    'div',
    { class: ['rt-frm__row', blocker && 'rt-frm__row--bad'] },
    h(
      'label',
      { class: 'rt-frm__label', for: id },
      h('span', { text: spec.label }),
      /* The workbook cell, always visible rather than hidden in a tooltip. The
         reader checking this against the spreadsheet on the next monitor is the
         reader this application is for. */
      h('span', { class: 'rt-frm__cell', text: spec.cell }),
    ),
    h(
      'span',
      { class: 'rt-frm__control' },
      h('input', {
        id,
        class: 'input rt-num',
        type: 'text',
        inputmode: 'decimal',
        value,
        disabled: !editable,
        placeholder: '—',
        oninput: (e: Event) => actions.setField(spec.key, (e.target as HTMLInputElement).value),
      }),
      h('span', { class: 'rt-frm__unit', text: spec.unit }),
      h(
        'select',
        {
          class: 'select select--sm',
          disabled: !editable,
          title: tier === undefined ? 'Where this value came from' : SOURCE_LABEL[tier],
          onchange: (e: Event) =>
            actions.setSource(spec.key, (e.target as HTMLSelectElement).value as SourceTier),
        },
        /* No blank option once a value is present: an unattributed number is
           what the platform rule exists to prevent, and the form defaults to
           `user` the moment somebody types. The blank only shows on an empty
           field, where there is nothing to attribute. */
        tier === undefined ? h('option', { value: '', text: '—', selected: true }) : null,
        ...SOURCE_TIERS.map((t) =>
          h('option', {
            value: t,
            text: SOURCE_MARK[t] === '' ? 'D' : SOURCE_MARK[t],
            title: SOURCE_LABEL[t],
            ...(tier === t ? { selected: true } : {}),
          }),
        ),
      ),
    ),
    blocker ? h('p', { class: 'rt-frm__why', text: blocker.why }) : null,
  );
}

/* --------------------------- the result --------------------------- */

function rank(rf: number): TagRank {
  return rf < 1 ? 4 : rf < 1.2 ? 3 : rf < 1.5 ? 2 : 1;
}

function resultPanel(s: Readonly<State>, result: RatingResult, preview: boolean, signed: number | null): HTMLElement {
  const governing = result.governing;

  return card(
    { pad: true },
    cardHead(
      'Rating',
      preview ? 'PREVIEW' : undefined,
      governing === null ? null : tag(`RF ${displayRf(governing.rf)}`, rank(governing.rf)),
    ),

    governing === null
      ? h('p', {
          class: 'rt-prose',
          text: 'Nothing could be rated: there is no live load.',
        })
      : h(
          'div',
          { class: 'rt-facts' },
          fact('Governing factor', displayRf(governing.rf)),
          fact('Method', governing.method === 'ASM' ? 'Allowable Stress' : 'Load Factor'),
          fact('Level', governing.level),
          fact('Section', governing.checkLabel),
          fact('Set by', governing.governedBy ?? '—'),
          fact('Posting', result.postingRequired ? 'Required' : 'Not required'),
        ),

    governing === null ? null : ratingCharts(s, { result, signed }),

    /* The whole table, not just the minimum. "The bridge rates 0.67" is not a
       report; which method, level and section produced it is. */
    h(
      'div',
      { class: 'rt-tbl rt-tbl--rate' },
      h(
        'div',
        { class: 'rt-tbl__head' },
        h('span', { text: 'Vehicle' }),
        h('span', { text: 'Method' }),
        h('span', { text: 'Inventory RF' }),
        h('span', { text: 'Inventory LR' }),
        h('span', { text: 'Operating RF' }),
        h('span', { text: 'Operating LR' }),
      ),
      ...['MS18', 'Type1-1', 'Type1-2', 'Type12-2'].flatMap((vehicle) =>
        (['LFM', 'ASM'] as const).map((method) => {
          const inv = result.table.find(
            (r) => r.vehicle === vehicle && r.method === method && r.level === 'inventory',
          );
          const op = result.table.find(
            (r) => r.vehicle === vehicle && r.method === method && r.level === 'operating',
          );
          const isGoverning = governing !== null && inv !== undefined && inv === governing;
          return h(
            'div',
            { class: ['rt-tbl__row', isGoverning && 'rt-tbl__row--gov'] },
            h('span', { text: method === 'LFM' ? vehicle : '' }),
            h('span', { text: method }),
            h('span', { text: inv ? displayRf(inv.rf) : '—' }),
            h('span', { text: inv ? `${displayTonnes(inv.lr)} t` : '—' }),
            h('span', { text: op ? displayRf(op.rf) : '—' }),
            h('span', { text: op ? `${displayTonnes(op.lr)} t` : '—' }),
          );
        }),
      ),
    ),

    cardHead('Posting'),
    h(
      'div',
      { class: 'rt-tbl rt-tbl--post' },
      h(
        'div',
        { class: 'rt-tbl__head' },
        h('span', { text: 'Vehicle' }),
        h('span', { text: 'Gross weight' }),
        h('span', { text: 'Posted at' }),
        h('span', { text: 'Limited by' }),
      ),
      ...result.posting.map((p) =>
        h(
          'div',
          { class: 'rt-tbl__row' },
          h('span', { text: p.vehicle.name }),
          h('span', { text: `${p.vehicle.tonnes} t` }),
          h(
            'span',
            null,
            p.unrestricted
              ? tag('Unrestricted', 1)
              : tag(`${displayTonnes(Math.max(0, p.tonnes))} t`, 4),
          ),
          h('span', {
            class: 'rt-tbl__sub',
            text: p.governedBy
              ? `${p.governedBy.method} ${p.governedBy.level}, ${p.governedBy.checkLabel}`
              : '—',
          }),
        ),
      ),
    ),
  );
}

/* --------------------------- the screen --------------------------- */

/** A card of the form, marked with the group the explorer's entries scroll to. */
function groupCard(group: string, ...children: Parameters<typeof card>[1][]): HTMLElement {
  const el = card({ pad: true }, ...children);
  el.dataset['group'] = group;
  return el;
}

/**
 * What the damage inputs rest on (LC-20, LC-21): the inspection, the defect
 * behind each value, and what is still assumed — with the one control that
 * records *inspected, no loss found* against the inspection.
 */
function damageCard(s: Readonly<State>): HTMLElement | null {
  const draft = s.draft;
  const bridge = draft.bridgeId === null ? null : s.bridges?.find((b) => b.id === draft.bridgeId) ?? null;
  if (bridge === null) return null;
  const span = Number(draft.spanIndex.trim());
  const fill = damageFromInspection(bridge, s.defects ?? [], {
    family: draft.family,
    position: draft.position,
    spanIndex: draft.spanIndex.trim() !== '' && Number.isInteger(span) ? span : null,
    girderCount: Number(draft.fields['girderCount']) || null,
  });
  const assumed = assumedDamage(draft.family, draft.sources, draft.damageBasis, draft.inspectionId);
  const inspection = bridge.inspections.find((i) => i.id === draft.inspectionId) ?? null;

  const card_ = card(
    { pad: true },
    cardHead(
      'From the inspection',
      inspection === null ? 'NO ACCEPTED INSPECTION' : `${inspection.type.toUpperCase()} · ${inspection.date}`,
      button('Inspected, no loss found', {
        sm: true,
        disabled: !can(s, 'rating.compute') || s.busy || assumed.length === 0 || draft.inspectionId === null,
        title:
          draft.inspectionId === null
            ? 'No accepted inspection is on record to have found no loss.'
            : `Record zero, against inspection ${draft.inspectionId}, for: ${assumed.map((k) => DAMAGE_LABEL[k] ?? k).join(', ') || 'nothing'}.`,
        onClick: () => actions.recordNoLoss(),
      }),
    ),
    h(
      'div',
      { class: 'rt-chips' },
      ...damageKeys(draft.family).map((key) => {
        const why = draft.sources[key] === 'inspection' ? draft.damageBasis[key] : undefined;
        const value = draft.fields[key] ?? '';
        return why === undefined
          ? tag(`${DAMAGE_LABEL[key]} · assumed`, assumed.includes(key) ? 3 : 'none', assumedDamageLine([key]) ?? SOURCE_LABEL[draft.sources[key] ?? 'user'])
          : why === NO_LOSS
            ? tag(`${DAMAGE_LABEL[key]} · none found`, 1, `Inspected, no loss found — ${draft.inspectionId}`)
            : tag(`${DAMAGE_LABEL[key]} · ${value} · ${why}`, 2, `From issue ${why}`);
      }),
    ),
    fill.notes.length > 0 ? h('ul', { class: 'rt-blk' }, ...fill.notes.map((note) => h('li', { text: note }))) : null,
  );
  card_.dataset['damage'] = assumed.length === 0 ? 'inspected' : 'assumed';
  return card_;
}

/** How many values a draft still wants before it can be saved. */
export function outstandingFor(draft: State['draft']): number {
  return assemble(draft).blockers.length;
}

/**
 * THE RATING FORM, for the page's property panel: what is being rated, the
 * layout and section, the materials and the note. The answer it produces is
 * the Rate screen, in the content panel beside it — see `rateScreen`.
 *
 * Of the structure in hand on the page. A draft begun on another structure is
 * not shown under this one's name; the form offers to begin this one instead.
 */
export function rateInputs(s: Readonly<State>, bridgeId: string | null): HTMLElement {
  const draft = s.draft;
  const bridge = bridgeId === null ? null : s.bridges?.find((b) => b.id === bridgeId) ?? null;
  const editable = can(s, 'rating.compute') && !s.busy;

  if (bridgeId === null || draft.bridgeId !== bridgeId) {
    const other = draft.bridgeId === null ? null : s.bridges?.find((b) => b.id === draft.bridgeId)?.name ?? draft.bridgeId;
    return h(
      'div',
      { class: 'rt-props' },
      card(
        { pad: true },
        cardHead('Load rating'),
        h('p', {
          class: 'rt-prose',
          text: other === null ? 'No rating of this structure is open.' : `The rating open is of ${other}.`,
        }),
        button('Begin a rating of this structure', {
          primary: true,
          disabled: bridgeId === null || !can(s, 'rating.compute'),
          ...(can(s, 'rating.compute') ? {} : { title: whyNot(s, 'compute a rating') }),
          onClick: () => bridgeId !== null && actions.startRating(bridgeId),
        }),
      ),
    );
  }

  const { input, blockers } = assemble(draft);
  const blockerFor = (key: string) => blockers.find((b) => b.key === key);
  /* The form's own field list, from one place — see `fieldsFor`. The material
     block is drawn separately because it is what the era table fills, so the
     layout half is everything that is not a material. */
  const allFields = fieldsForDraft(draft);
  const layoutFields = allFields.filter((f) => !MATERIAL_FIELDS.includes(f));
  const year = bridge?.year ?? null;
  const eraConcrete = year === null ? null : eraRowFor(s.profile.eras.concrete, year);

  return h(
    'div',
    { class: 'rt-props' },

      groupCard('what',
        cardHead('What is being rated'),
        h(
          'div',
          { class: 'rt-facts' },
          fact('Structure', `${bridge?.name ?? '—'} (${draft.bridgeId})`),
          fact('Superstructure', bridge?.type ?? '—'),
          fact('Continuity', 'Simple'),
          fact('Year built', year === null ? '—' : String(year)),
        ),

        /* THE FAMILY PICKER OFFERS ONLY WHAT THIS BUILD CAN RATE, rather than
           drawing the others as dead options: a picker with live and dead
           entries side by side invites somebody to click a dead one. */
        h(
          'div',
          /* `--choice` marks a row that picks a shape rather than carrying a
             value: it has no provenance and no workbook cell, so the checks that
             count fields exclude it. */
          { class: 'rt-frm__row rt-frm__row--choice' },
          h('label', { class: 'rt-frm__label' }, h('span', { text: 'Family' })),
          h(
            'span',
            { class: 'rt-frm__control' },
            seg(
              FAMILIES.map((f) => ({
                label: f,
                on: draft.family === f,
                title: FAMILY_LABEL[f],
                onClick: () => actions.setFamily(f),
              })),
            ),
          ),
        ),

        /* Only the girder families ask. RCFS has no girders and the API refuses
           a position on one — and BOTH girder families must answer, because
           `assemble` blocks on a missing position and the API refuses a PCDG
           rating without one. This asked RCDG alone for a while, which left the
           PCDG form permanently one value short of saving with nothing on
           screen to supply it. */
        draft.family !== 'RCFS'
          ? h(
              'div',
              {
                class: [
                  'rt-frm__row',
                  'rt-frm__row--choice',
                  blockerFor('position') && 'rt-frm__row--bad',
                ],
              },
              h('label', { class: 'rt-frm__label' }, h('span', { text: 'Which girder' })),
              h(
                'span',
                { class: 'rt-frm__control' },
                seg(
                  (['interior', 'exterior'] as const).map((pos) => ({
                    label: pos === 'interior' ? 'Interior' : 'Exterior',
                    on: draft.position === pos,
                    onClick: () => actions.setPosition(pos),
                  })),
                ),
              ),
              blockerFor('position')
                ? h('p', { class: 'rt-frm__why', text: blockerFor('position')!.why })
                : null,
            )
          : null,

      ),

      groupCard('layout',
        cardHead('Layout and section', undefined,
          button(s.busy ? 'Reading…' : 'Fill from model', {
            sm: true,
            disabled: !editable,
            title:
              'Read this structure’s CSiBridge cloud model — the design document itself, so the values it fills are marked as coming from the model rather than transcribed.',
            onClick: () => void actions.fillFromModel(),
          }),
          button('Fill conventional', {
            sm: true,
            disabled: !editable,
            title: 'The unit weights every workbook in the reference pack uses, and the flat slab’s one-metre strip. Recorded as entered by you, because somebody chose them.',
            onClick: () => actions.fillConventional(),
          }),
        ),
        ...layoutFields.map((spec) =>
          fieldRow(spec, draft.fields[spec.key] ?? '', draft.sources[spec.key], blockerFor(spec.key), editable),
        ),
      ),

      /* PCDG asks for no allowables — see PCDG_FIELDS — so its form has no
         material block; drawing one would be six boxes nothing reads. */
      draft.family === 'PCDG' ? null : groupCard('materials',
        cardHead('Materials', undefined,
          button('Fill from year built', {
            sm: true,
            disabled: !editable || eraConcrete === null,
            title:
              eraConcrete === null
                ? 'The register has no year of construction, so the era table cannot be applied.'
                : `The ${eraConcrete.label} row of the profile's material table.`,
            onClick: () => actions.fillFromEra(),
          }),
        ),
        ...MATERIAL_FIELDS.map((spec) =>
          fieldRow(spec, draft.fields[spec.key] ?? '', draft.sources[spec.key], blockerFor(spec.key), editable),
        ),
      ),

      groupCard('note',
        cardHead('Note'),
        h('textarea', {
          class: 'input input--tall',
          rows: 4,
          disabled: !editable,
          placeholder: 'What was assumed, and why.',
          value: draft.note,
          oninput: (e: Event) => actions.setNote((e.target as HTMLTextAreaElement).value),
        }),
      ),

      saveBar(s, input),
  );
}

/**
 * Save, docked at the foot of the form. The deployment recomputes and stores
 * its own answer; the button is not offered until nothing is outstanding.
 */
function saveBar(s: Readonly<State>, input: unknown): HTMLElement {
  const draft = s.draft;
  const unsavable = unsavableDamage(draft.sources, draft.damageBasis);
  return h(
    'div',
    { class: 'rt-savebar' },
    s.error !== null ? h('p', { class: 'rt-frm__why', role: 'alert', text: s.error }) : null,
    unsavable !== null ? h('p', { class: 'rt-frm__why', role: 'alert', 'data-unsavable': '', text: unsavable }) : null,
    button(s.busy ? 'Saving…' : s.saved !== null ? `Saved · ${s.saved.state}` : 'Save rating', {
      primary: true,
      block: true,
      disabled:
        input === null ||
        s.saved !== null ||
        !can(s, 'rating.compute') ||
        s.busy ||
        s.backend !== 'live' ||
        unsavable !== null,
      ...(!can(s, 'rating.compute')
        ? { title: whyNot(s, 'compute a rating') }
        : input === null
          ? { title: `${outstandingFor(draft)} value(s) still outstanding` }
          : {}),
      onClick: () => void actions.save(input),
    }),
  );
}

/**
 * The answer, for the content panel: what is still wanted, the result as it is
 * computed, saving it, and the corrections applied. The inputs are in the
 * property panel beside it — see `rateInputs`.
 */
export function rateScreen(s: Readonly<State>): HTMLElement {
  const draft = s.draft;
  const bridge = draft.bridgeId === null ? null : s.bridges?.find((b) => b.id === draft.bridgeId) ?? null;

  if (draft.bridgeId === null) {
    return h(
      'div',
      { class: 'rt-scr' },
      card(
        { pad: true },
        h('p', {
          class: 'rt-prose',
          text: 'No rating is open.',
        }),
      ),
    );
  }

  const { input, blockers } = assemble(draft);

  /* Computed here for the screen. The engine is pure and this is cheap — one
     rating is a few dozen multiplications — so there is nothing to debounce. */
  let preview: RatingResult | null = null;
  let previewError: string | null = null;
  if (input !== null) {
    try {
      /* The same dispatch the server makes, on the same field — see
         functions/lib/ratings.ts. This one is only for the screen. */
      preview =
        draft.family === 'RCDG'
          ? rateRcdgSimple(input as RcdgInput, s.profile).result
          : draft.family === 'PCDG'
            ? ratePcdgSimple(input as PcdgInput, s.profile).result
            : rateRcfsSimple(input as RcfsInput, s.profile).result;
    } catch (err) {
      previewError = err instanceof Error ? err.message : String(err);
    }
  }

  return h(
    'div',
    { class: 'rt-scr' },
    h(
      'div',
      { class: 'rt-scr__right' },

      s.modelFill === null
        ? null
        : card(
            { pad: true },
            cardHead(
              'From the model',
              [
                s.modelFill.readable ? null : 'NOT READABLE',
                /* LC-12: the model of record, or the newest where none is set. */
                s.modelFill.modelOfRecord === false ? 'NO MODEL OF RECORD SET' : null,
              ]
                .filter(Boolean)
                .join(' · ') || undefined,
            ),
            s.modelFill.about?.model
              ? h(
                  'div',
                  { class: 'rt-facts rt-facts--tight' },
                  fact('Model', s.modelFill.about.model),
                  fact('Type', s.modelFill.about.bridgeType ?? '—'),
                  fact('Exported', (s.modelFill.about.exported ?? '—').slice(0, 10)),
                  fact('By', s.modelFill.about.generator ?? '—'),
                )
              : null,
            /* THE NOTES ARE HALF THE ANSWER. A fill that quietly left eleven
               fields blank would read as a failure; the model saying which
               values it does not carry, and why, is what an engineer acts on. */
            (s.modelFill.notes ?? []).length > 0
              ? h(
                  'ul',
                  { class: 'rt-blk' },
                  ...(s.modelFill.notes ?? []).map((note) => h('li', { text: note })),
                )
              : null,
          ),

      damageCard(s),

      blockers.length > 0
        ? card(
            { pad: true },
            cardHead('Not yet complete', `${blockers.length} VALUE${blockers.length === 1 ? '' : 'S'} OUTSTANDING`),
            h(
              'ul',
              { class: 'rt-blk rt-blk--why' },
              ...blockers.map((b) => h('li', { text: b.why })),
            ),
          )
        : previewError !== null
          ? card({ pad: true }, cardHead('The engine refused'), h('p', { class: 'rt-prose', text: previewError }))
          : preview !== null
            ? resultPanel(s, s.saved?.result ?? preview, s.saved === null, bridge?.loadRestriction ?? null)
            : null,

      s.saved !== null
        ? card(
            { pad: true },
            cardHead('Saved', `${s.saved.state.toUpperCase()} · ENGINE ${s.saved.engineVersion}`),
            h(
              'div',
              { class: 'rt-facts' },
              fact('Rated by', s.saved.ratedBy ?? 'not identified'),
              fact('Rated at', s.saved.ratedAt),
              fact('Profile', `${s.saved.profileId} ${s.saved.profileVersion}`),
              fact('Exterior DF rule', s.saved.exteriorDf ?? 'not applicable'),
            ),
            h(
              'div',
              { class: 'rt-btnrow' },
              button('Send for review', {
                primary: true,
                disabled: s.saved.state !== 'draft' || s.busy,
                onClick: () => void actions.moveState(s.saved!.id, 'review'),
              }),
              (() => {
                /* LC-21: the deployment refuses it, and the button says so first. */
                const assumed = assumedDamageLine(
                  assumedDamage(s.saved.family, s.saved.sources, s.saved.damageBasis ?? {}, s.saved.inspectionId ?? null),
                );
                return button('Accept', {
                  disabled: s.saved.state !== 'review' || !can(s, 'rating.accept') || s.busy || assumed !== null,
                  ...(!can(s, 'rating.accept')
                    ? { title: whyNot(s, 'accept a rating') }
                    : assumed !== null
                      ? { title: assumed }
                      : {}),
                  onClick: () => void actions.moveState(s.saved!.id, 'accepted'),
                });
              })(),
            ),
          )
        : card(
            { pad: true },
            cardHead('Save'),
            unsavableDamage(draft.sources, draft.damageBasis)
              ? h('p', { class: 'rt-prose', role: 'alert', 'data-unsavable': '', text: unsavableDamage(draft.sources, draft.damageBasis)! })
              : null,
            button(s.busy ? 'Saving…' : 'Save rating', {
              primary: true,
              block: true,
              disabled:
                input === null ||
                !can(s, 'rating.compute') ||
                s.busy ||
                s.backend !== 'live' ||
                unsavableDamage(draft.sources, draft.damageBasis) !== null,
              ...(can(s, 'rating.compute') ? {} : { title: whyNot(s, 'compute a rating') }),
              onClick: () => void actions.save(input),
            }),
          ),

      card(
        { pad: true },
        cardHead('Corrections applied'),
        h(
          'ul',
          { class: 'rt-blk rt-blk--corr' },
          ...correctionsFor(draft.family).map((c) =>
            h(
              'li',
              null,
              h('strong', { text: `${c.id} · ${c.where}` }),
              h('span', {
                class: 'rt-blk__state',
                text: c.applied ? 'corrected' : 'reproduced as written',
              }),
              h('p', { text: c.effect }),
            ),
          ),
        ),
      ),
    ),
  );
}
